import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { useUserStore } from './user'
import type { Credentials } from '@/types/user'
import {
  validateAndStoreToken,
  getRoleFromMultipleSources,
  syncRoleToUserStore,
  extractRoleFromToken,
  getRoleFromCookie
} from '@/utils/roleHelpers'
import { decodeJWT } from '@/utils/jwtDecoder'
import { identifyPosthogUser, resetPosthog } from '@/utils/posthogAnalytics'

export const useAuthStore = defineStore('auth', () => {
  // Reactive state
  const isAuthenticated = ref(false)
  const user = ref(null)
  const error = ref<string | null>(null)
  const isLoading = ref(false)
  // Reactive user role - critical for cross-subdomain auth
  // This is updated when role is extracted from JWT token or cookies
  const userRole = ref<string | null>(null)
  // Reactive admin flag - hydrated from localStorage on init, updated when new JWTs arrive.
  // Persisted separately from the JWT so it survives token expiration/removal.
  const isAdmin = ref(localStorage.getItem('is_admin') === 'true')

  /**
   * Extract is_admin from a JWT token and persist it.
   * Called only when a new token is received (login, refresh, social auth).
   */
  const updateAdminFromToken = (token: string | null) => {
    if (!token) return
    try {
      const decoded = decodeJWT(token)
      if (decoded?.is_admin === true) {
        isAdmin.value = true
        localStorage.setItem('is_admin', 'true')
      }
    } catch (err) {
      console.warn('[Auth] Failed to decode token for admin check:', err)
    }
  }

  // eslint-disable-next-line complexity
  const login = async (credentials: Credentials) => {
    const userStore = useUserStore()
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post(API_ROUTES.USER_LOGIN, credentials)
      
      // Cookies are set automatically by backend
      // Store tokens in localStorage as backup, but only if they're valid JWT format
      // Extract role from tokens FIRST (before checking id), so we can use it even if token isn't stored
      let tokenRole: string | null = null
      
      if (response.data.jwt_access) {
        const result = validateAndStoreToken(response.data.jwt_access, 'access')
        tokenRole = result.role
        updateAdminFromToken(response.data.jwt_access)
      }
      
      if (response.data.jwt_refresh) {
        validateAndStoreToken(response.data.jwt_refresh, 'refresh')
      }
      
      isAuthenticated.value = true
      user.value = response.data.user
      
      // Set userRole from token if available
      if (tokenRole) {
        userRole.value = tokenRole
      }
      
      // Store user data from login response directly in userStore
      // This avoids redundant API call and prevents login failure if fetchCurrentUser fails
      if (response.data.user) {
        // Pinia auto-unwraps refs, so assign directly
        userStore.currentUser = response.data.user
        
        // Extract role from JWT token if user data doesn't have it
        const currentUser = userStore.currentUser
        if (currentUser) {
          const hasRole = currentUser.role && currentUser.role.trim() !== ''
          
          if (hasRole && !userRole.value && currentUser.role) {
            // Set userRole from user data if not already set from token
            userRole.value = currentUser.role.toUpperCase()
          } else if (!hasRole) {
            // Try to get role from multiple sources
            const roleFromSources: string | null = await getRoleFromMultipleSources(userStore, tokenRole || response.data.jwt_access || null)
            if (roleFromSources) {
              userRole.value = roleFromSources
              console.debug('[Auth] Role synced to userStore:', roleFromSources)
            } else {
              console.warn('[Auth] Role not found in user data, JWT token, or API response')
            }
          }
        }
      } else {
        // If user data not in response, try to get role from multiple sources
        const roleFromSources: string | null = await getRoleFromMultipleSources(userStore, tokenRole || response.data.jwt_access)
        
        if (roleFromSources && !userRole.value) {
          userRole.value = roleFromSources
        }
        
        // Try to fetch complete user data (but don't fail login if it fails)
        try {
          await userStore.fetchCurrentUser()
          // Sync role if we have it but user data doesn't
          if (roleFromSources && userStore.currentUser) {
            const currentUser = userStore.currentUser
            if (!currentUser.role) {
              syncRoleToUserStore(userStore, roleFromSources)
            }
          }
        } catch (err) {
          console.warn('Failed to fetch user data after login, but login succeeded:', err)
        }
      }

      identifyPosthogUser(
        userStore.currentUser ?? response.data.user ?? undefined,
        userRole.value
      )

      return response.data
    } catch (err: any) {
      // Extract error message from various possible response formats
      let errorMessage = 'An unexpected error occurred. Please try again later.'
      
      if (err.response) {
        // Handle different error response formats
        const responseData = err.response.data
        
        if (responseData?.detail) {
          // Django REST framework format
          errorMessage = typeof responseData.detail === 'string' 
            ? responseData.detail 
            : 'The email or password you entered is incorrect'
        } else if (responseData?.message) {
          // Custom message format
          errorMessage = responseData.message
        } else if (responseData?.error) {
          // Error field format
          errorMessage = typeof responseData.error === 'string'
            ? responseData.error
            : 'The email or password you entered is incorrect'
        } else if (responseData?.non_field_errors) {
          // Django non-field errors format
          errorMessage = Array.isArray(responseData.non_field_errors)
            ? responseData.non_field_errors[0]
            : responseData.non_field_errors
        } else if (err.response.status === 401) {
          errorMessage = 'The email or password you entered is incorrect'
        } else if (err.response.status === 400) {
          errorMessage = 'Invalid credentials. Please check your email and password.'
        } else if (err.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.'
        }
      } else if (!err.response) {
        // Network error
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          errorMessage = 'Request timed out. Please check your connection and try again.'
        } else {
          errorMessage = 'Unable to reach the server. Please check your connection and try again.'
        }
      }
      
      error.value = errorMessage
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update userRole from JWT token or cookie (reactive)
   * This is called when role is extracted from token/cookie
   * Priority: cookie (cross-subdomain) -> localStorage token
   */
  const updateUserRoleFromToken = () => {
    // Priority 1: Check user_role cookie first (available cross-subdomain)
    const cookieRole = getRoleFromCookie()
    if (cookieRole) {
      userRole.value = cookieRole
      console.debug('[Auth] Updated userRole from cookie:', cookieRole)
      return cookieRole
    }
    
    // Priority 2: Try localStorage token
    const token = localStorage.getItem('token')
    if (token) {
      const role = extractRoleFromToken(token)
      if (role) {
        userRole.value = role
        console.debug('[Auth] Updated userRole from JWT:', role)
        return role
      }
    }
    
    return null
  }

  const logout = async () => {
    try {
      // Call logout endpoint to delete cookies
      await api.post(API_ROUTES.USER_LOGOUT)
    } catch (err) {
      // Even if logout endpoint fails, continue with cleanup
      console.error('Logout endpoint error:', err)
    } finally {
      resetPosthog()

      // Clear all localStorage items
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_id')
      localStorage.removeItem('is_admin')
      
      // Clear auth state
      isAuthenticated.value = false
      user.value = null
      userRole.value = null
      isAdmin.value = false
      
      // Clear current user data
      const userStore = useUserStore()
      userStore.clearCurrentUser()
    }
  }

  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token')
      const refreshPayload = refreshTokenValue ? { jwt_refresh: refreshTokenValue } : {}
      
      const response = await api.post(API_ROUTES.USER_JWT_REFRESH, refreshPayload)
      
      if (response.data.jwt_access) {
        localStorage.setItem('token', response.data.jwt_access)
        updateAdminFromToken(response.data.jwt_access)
        return response.data.jwt_access
      }
      
      return null
    } catch (err) {
      // If refresh fails, clear tokens
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_id')
      isAuthenticated.value = false
      throw err
    }
  }

  // eslint-disable-next-line complexity
  const checkAuth = async (): Promise<boolean> => {
    const userStore = useUserStore()
    
    try {
      // Verify authentication via /user-get-current endpoint
      // For cross-subdomain scenarios, rely on cookies (not localStorage tokens)
      // The request interceptor will validate tokens and only send valid ones
      // If token is invalid/missing, rely on cookies (withCredentials: true is set in axios config)
      const response = await api.get(API_ROUTES.USER_GET_CURRENT)
      
      // If we reach here without an error, the request succeeded (axios throws for non-2xx status)
      // A successful response (status 200-299) indicates authentication succeeded
      // We validate the status explicitly to be safe, but axios would have thrown if it wasn't 2xx
      const isSuccessful = response.status >= 200 && response.status < 300
      
      if (isSuccessful) {
        isAuthenticated.value = true
        
        // Store user data in userStore so router guards can access role information
        // This is critical for role-based routing to work correctly
        if (response.data != null) {
          // Pinia auto-unwraps refs, so assign directly
          userStore.currentUser = response.data

          // Cookie-only sessions (social auth) never expose a JWT, so take admin from the API.
          isAdmin.value = response.data.admin === true
          localStorage.setItem('is_admin', String(isAdmin.value))
          
          // Try to get role from existing sources first
          let roleFound = false
          const currentUser = userStore.currentUser
          
          // Priority 1: Check if role is in user data from API response
          if (currentUser?.role && currentUser.role.trim() !== '') {
            userRole.value = currentUser.role.toUpperCase()
            roleFound = true
            console.debug('[Auth] Role from API response:', userRole.value)
          }
          
          // Priority 2: Try to get role from JWT token in response
          if (!roleFound && response.data.jwt_access) {
            const role = extractRoleFromToken(response.data.jwt_access)
            if (role) {
              userRole.value = role
              syncRoleToUserStore(userStore, role)
              roleFound = true
              console.debug('[Auth] Role from JWT in response:', role)
            }
          }
          
          // Priority 3: Check user_role cookie (readable, available cross-subdomain)
          // Backend sets this cookie with httponly=False, so JavaScript CAN read it
          if (!roleFound) {
            const cookieRole = getRoleFromCookie()
            if (cookieRole) {
              userRole.value = cookieRole
              syncRoleToUserStore(userStore, cookieRole)
              roleFound = true
              console.debug('[Auth] Role from user_role cookie:', cookieRole)
            }
          }
          
          // Priority 4: Try to get role from localStorage token (if available)
          if (!roleFound) {
            const role = extractRoleFromToken(localStorage.getItem('token'))
            if (role) {
              userRole.value = role
              syncRoleToUserStore(userStore, role)
              roleFound = true
              console.debug('[Auth] Role from localStorage token:', role)
            }
          }
          
          // Priority 5: CROSS-SUBDOMAIN FIX - If no role found yet, try to get fresh JWT via refresh
          // This handles cases where:
          // - localStorage has no JWT (not shared across subdomains)
          // - user_role cookie might not be accessible (domain mismatch, etc.)
          // - But refresh_token cookie IS available and can get us a fresh JWT
          if (!roleFound) {
            const hasLocalStorageToken = !!localStorage.getItem('token')
            
            // Only try refresh if we don't have a token in localStorage
            // (cross-subdomain scenario)
            if (!hasLocalStorageToken) {
              try {
                console.debug('[Auth] Cross-subdomain auth: No role found from cookie/token, attempting token refresh...')
                // Try refreshing token - backend will use refresh cookie if available
                const refreshResponse = await api.post(API_ROUTES.USER_JWT_REFRESH, {})
                
                if (refreshResponse.data.jwt_access) {
                  // Store the fresh token
                  const result = validateAndStoreToken(refreshResponse.data.jwt_access, 'access')
                  
                  if (result.role) {
                    userRole.value = result.role
                    syncRoleToUserStore(userStore, result.role)
                    console.debug('[Auth] Updated userRole from refreshed JWT:', result.role)
                    roleFound = true
                  } else {
                    console.warn('[Auth] Refreshed token does not contain role claim')
                  }
                  
                  // Also store refresh token if provided
                  if (refreshResponse.data.jwt_refresh) {
                    validateAndStoreToken(refreshResponse.data.jwt_refresh, 'refresh')
                  }
                } else {
                  console.warn('[Auth] Token refresh succeeded but no jwt_access in response')
                }
              } catch (refreshErr) {
                // Refresh failed, but user is still authenticated (checkAuth succeeded)
                console.warn('[Auth] Cross-subdomain auth: Could not refresh token to get role.', refreshErr)
              }
            }
          }
          
          if (!roleFound) {
            console.warn('[Auth] Role not found in API response, JWT token, cookie, or refresh - backend may not be including role field')
          }
          
          // Debug logging to help identify role issues
          console.debug('[Auth] User data received from /user-get-current:', {
            id: response.data.id,
            email: response.data.email,
            role: userStore.currentUser?.role || response.data.role,
            has_role: !!userStore.currentUser?.role
          })
          
          // If backend returned tokens, validate and store them in localStorage as backup
          // This is important for cross-subdomain scenarios where cookies work but localStorage doesn't
          if (response.data.jwt_access) {
            updateAdminFromToken(response.data.jwt_access)
            try {
              const { decodeJWT } = await import('@/utils/jwtDecoder')
              const decoded = decodeJWT(response.data.jwt_access)
              // Only store token if it's valid JWT format, not expired, AND has required 'id' field
              if (decoded && decoded.exp) {
                const isExpired = decoded.exp * 1000 < Date.now()
                const hasId = decoded.id !== undefined && decoded.id !== null
                
                if (isExpired) {
                  console.warn('[Auth] Token from checkAuth is expired, not storing')
                } else if (!hasId) {
                  console.warn('[Auth] Token from checkAuth missing required id field, not storing - relying on cookies')
                } else {
                  localStorage.setItem('token', response.data.jwt_access)
                  console.debug('[Auth] Token from checkAuth stored successfully with id:', decoded.id)
                }
              } else {
                console.warn('[Auth] Token from checkAuth missing exp field, not storing')
              }
            } catch (err) {
              console.warn('[Auth] Failed to validate token from checkAuth:', err)
            }
          }
          if (response.data.jwt_refresh) {
            try {
              const { decodeJWT } = await import('@/utils/jwtDecoder')
              const decoded = decodeJWT(response.data.jwt_refresh)
              // Only store token if it's valid JWT format, not expired, AND has required 'id' field
              if (decoded && decoded.exp) {
                const isExpired = decoded.exp * 1000 < Date.now()
                const hasId = decoded.id !== undefined && decoded.id !== null
                
                if (isExpired) {
                  console.warn('[Auth] Refresh token from checkAuth is expired, not storing')
                } else if (!hasId) {
                  console.warn('[Auth] Refresh token from checkAuth missing required id field, not storing - relying on cookies')
                } else {
                  localStorage.setItem('refresh_token', response.data.jwt_refresh)
                  console.debug('[Auth] Refresh token from checkAuth stored successfully with id:', decoded.id)
                }
              } else {
                console.warn('[Auth] Refresh token from checkAuth missing exp field, not storing')
              }
            } catch (err) {
              console.warn('[Auth] Failed to validate refresh token from checkAuth:', err)
            }
          }
        }
        
        return true
      }
      
      // This should rarely happen since axios throws for non-2xx, but handle it just in case
      isAuthenticated.value = false
      return false
    } catch (err: any) {
      // If check fails, clear tokens and set authenticated to false
      // Don't clear tokens on network errors - might be temporary
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        console.warn('Auth check timed out, keeping existing auth state')
        return isAuthenticated.value
      }
      
      // Only clear tokens on actual auth failures (401, 403, etc)
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Clear invalid tokens
        localStorage.removeItem('token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user_id')
        isAuthenticated.value = false
        userStore.clearCurrentUser()
      } else if (!err.response) {
        // Network error - don't clear auth state, might be temporary
        console.warn('Network error during auth check:', err.message)
        return isAuthenticated.value
      }
      
      return false
    }
  }

  const initAuth = async () => {
    // Always check auth status, even if no token in localStorage
    // This handles cross-subdomain scenarios where:
    // - Cookies are shared across subdomains
    // - localStorage is NOT shared across subdomains
    const isAuthed = await checkAuth()

    if (isAuthed) {
      const userStore = useUserStore()

      // If userRole is already set from checkAuth, skip role sync
      if (userRole.value) {
        console.debug('[Auth] userRole already set from checkAuth:', userRole.value)
      } else {
        const hasRole = userStore.currentUser?.role && userStore.currentUser.role.trim() !== ''

        if (hasRole && userStore.currentUser?.role) {
          userRole.value = userStore.currentUser.role.toUpperCase()
          console.debug('[Auth] userRole set from currentUser in initAuth:', userRole.value)
        } else {
          console.debug('[Auth] Role not set in currentUser, attempting to get from multiple sources...')
          const role = await getRoleFromMultipleSources(userStore)
          if (role) {
            userRole.value = role
            console.debug('[Auth] Role synced in initAuth:', role)
          } else {
            updateUserRoleFromToken()
            if (userRole.value) {
              console.debug('[Auth] userRole set from token/cookie in initAuth:', userRole.value)
            } else {
              console.warn('[Auth] Could not determine role from any source')
            }
          }
        }
      }

      if (userStore.currentUser) {
        identifyPosthogUser(userStore.currentUser, userRole.value)
      }
    }
  }

  const setError = (message: string | null) => {
    error.value = message
  }

  /**
   * Set tokens from social auth callback
   * Used when receiving tokens from OAuth redirect
   */
  const setTokens = async (tokens: { accessToken: string; refreshToken?: string }) => {
    const userStore = useUserStore()
    
    try {
      // Store tokens in localStorage
      if (tokens.accessToken) {
        console.log('[Auth] Validating and storing access token...')
        const result = validateAndStoreToken(tokens.accessToken, 'access')
        if (result.role) {
          userRole.value = result.role
          console.log('[Auth] Role extracted from token:', result.role)
        }
        updateAdminFromToken(tokens.accessToken)
      }
      
      if (tokens.refreshToken) {
        console.log('[Auth] Storing refresh token...')
        validateAndStoreToken(tokens.refreshToken, 'refresh')
      }
      
      // Set authenticated state
      isAuthenticated.value = true
      console.log('[Auth] Authentication state set to true')
      
      // Fetch user data with timeout to prevent hanging
      try {
        console.log('[Auth] Fetching current user...')
        const fetchPromise = userStore.fetchCurrentUser()
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Fetch user timeout')), 5000)
        )
        
        await Promise.race([fetchPromise, timeoutPromise])
        console.log('[Auth] User data fetched successfully')
        
        // Sync role if needed
        if (userStore.currentUser) {
          const currentUser = userStore.currentUser
          if (currentUser.role && !userRole.value) {
            userRole.value = currentUser.role.toUpperCase()
          }
        }
      } catch (err) {
        console.warn('[Auth] Failed to fetch user data after social auth (continuing anyway):', err)
      }

      if (userStore.currentUser) {
        identifyPosthogUser(userStore.currentUser, userRole.value)
      }

      return true
    } catch (err) {
      console.error('[Auth] Failed to set tokens from social auth:', err)
      return false
    }
  }

  return {
    // State
    isAuthenticated,
    user,
    error,
    isLoading,
    userRole, // Reactive user role for cross-subdomain auth
    isAdmin,  // Reactive admin flag from JWT is_admin claim
    // Actions
    login,
    logout,
    refreshToken,
    checkAuth,
    initAuth,
    setError,
    updateUserRoleFromToken,
    setTokens
  }
}) 